import { countryGroups } from '@modules/internationalisation/countryGroup';
import { addQueryParamsToURL, getAllQueryParams, getOrigin, getPaperOrigin, isProd, } from './url';
const routes = {
    recurringContribCheckout: '/contribute/recurring',
    recurringContribCreate: '/contribute/recurring/create',
    recurringContribPending: '/contribute/recurring/pending',
    contributionsSendMarketing: '/contribute/send-marketing',
    getUserType: '/identity/get-user-type',
    contributionsMarketingConfirm: '/contribute/marketing-confirm',
    payPalSetupPayment: '/paypal/setup-payment',
    payPalCreateAgreement: '/paypal/create-agreement',
    payPalOneClickCheckout: '/paypal/one-click-checkout',
    directDebitCheckAccount: '/direct-debit/check-account',
    payPalRestReturnURL: '/paypal/rest/return',
    subscriptionCreate: '/subscribe/create',
    subscriptionsLanding: '/subscribe',
    checkout: '/checkout',
    digitalSubscriptionLanding: '/subscribe/digitaledition',
    paperSubscriptionLanding: '/subscribe/paper',
    paperSubscriptionProductChoices: '/subscribe/paper#HomeDelivery',
    paperSubscriptionDeliveryProductChoices: '/subscribe/paper/delivery#HomeDelivery',
    guardianWeeklySubscriptionLanding: '/subscribe/weekly',
    guardianWeeklySubscriptionLandingGift: '/subscribe/weekly/gift',
    guardianWeeklyStudentUK: 'https://connect.studentbeans.com/v4/hosted/the-guardian-weekly/uk',
    guardianWeeklyStudentAU: 'https://connect.studentbeans.com/v4/hosted/the-guardian-weekly/au',
    supporterPlusStudentBeansUk: 'https://www.studentbeans.com/en-gb/uk/beansid-connect/hosted/the-guardiandigital',
    supporterPlusStudentBeansUs: 'https://www.studentbeans.com/en-us/us/beansid-connect/hosted/the-guardian-digital/student/e15f676f-99cb-492d-9d95-ff17af36f274',
    supporterPlusStudentBeansCa: 'https://www.studentbeans.com/en-ca/ca/beansid-connect/hosted/the-guardian-digital/student/362bcbd6-b491-4adf-8ca1-9f0c9f69c3b7',
    supporterPlusStudentBeansEu: '/eu/checkout?product=SupporterPlus&ratePlan=OneYearStudent&backButton=false',
    postcodeLookup: '/api/postcode-lookup',
    createSignInUrl: '/identity/signin-url',
    stripeSetupIntentRecaptcha: '/stripe/create-setup-intent/recaptcha',
};
const createOneOffReminderEndpoint = isProd()
    ? 'https://support.theguardian.com/reminders/create/one-off'
    : 'https://support.code.dev-theguardian.com/reminders/create/one-off';
const createRecurringReminderEndpoint = isProd()
    ? 'https://support.theguardian.com/reminders/create/recurring'
    : 'https://support.code.dev-theguardian.com/reminders/create/recurring';
const countryPath = (countryGroupId) => countryGroups[countryGroupId].supportRegionId;
function postcodeLookupUrl(postcode) {
    return `${getOrigin()}${routes.postcodeLookup}/${encodeURIComponent(postcode)}`;
}
function paperSubsUrl(paperFulfulmentOption, promoCode) {
    const baseURL = [getOrigin(), 'uk/subscribe/paper'].join('/');
    const queryParams = [
        ...getAllQueryParams(),
        ...(promoCode ? [['promoCode', promoCode]] : []),
    ];
    const queryParamsString = queryParams
        .map((keyValuePair) => keyValuePair.join('='))
        .join('&');
    const hash = paperFulfulmentOption ? `#${paperFulfulmentOption}` : '';
    if (queryParamsString) {
        return `${baseURL}?${queryParamsString}${hash}`;
    }
    return baseURL;
}
function getDigitalPlusCheckoutDeepLink(countryGroupId, billingPeriod) {
    const urlParams = {
        product: 'DigitalSubscription',
        ratePlan: billingPeriod ?? 'Monthly',
        utm_medium: 'ACQUISITIONS_SUPPORT_SITE',
        utm_campaign: 'digitalplus',
        utm_content: `acquisition_${billingPeriod?.toLowerCase()}`,
        utm_term: 'printcheckout',
        utm_source: 'GUARDIAN_WEB',
        backLocation: 'subscribe',
    };
    const domain = getOrigin();
    const localePath = countryPath(countryGroupId);
    const checkoutRoute = routes.checkout;
    const queryParams = new URLSearchParams(Object.entries(urlParams)).toString();
    return `${domain}/${localePath}${checkoutRoute}?${queryParams}`;
}
function guardianWeeklyLanding(countryGroupId, gift) {
    const url = `${getOrigin()}/${countryPath(countryGroupId)}${gift
        ? routes.guardianWeeklySubscriptionLandingGift
        : routes.guardianWeeklySubscriptionLanding}`;
    return url;
}
const promotionTermsUrl = (promoCode) => `${getOrigin()}/p/${promoCode}/terms`;
function paperCheckoutUrl(fulfilmentOption, productOptions, promoCode, abTestName) {
    const url = `${getPaperOrigin(productOptions)}/uk/checkout`;
    const params = abTestName
        ? {
            [abTestName]: 'true',
            promoCode,
            product: fulfilmentOption === 'Collection'
                ? 'SubscriptionCard'
                : fulfilmentOption,
            ratePlan: productOptions,
        }
        : {
            promoCode,
            product: fulfilmentOption === 'Collection'
                ? 'SubscriptionCard'
                : fulfilmentOption,
            ratePlan: productOptions,
        };
    return addQueryParamsToURL(url, params);
}
function parameteriseUrl(url, promoCode, fulfilmentOption) {
    const params = {
        promoCode,
    };
    const urlWithParams = addQueryParamsToURL(url, params);
    return fulfilmentOption
        ? `${urlWithParams}#${fulfilmentOption}`
        : urlWithParams;
}
// If the user cancels before completing the payment flow, send them back to the contribute page.
function payPalCancelUrl(cgId) {
    return `${getOrigin()}/${countryPath(cgId)}/contribute`;
}
function payPalReturnUrl(cgId, email, route = '/paypal/rest/return') {
    return `${getOrigin()}/${countryPath(cgId)}${route}?email=${encodeURIComponent(email)}`;
}
function stripePayPalReturnUrl(cgId, email, stripePublicKey, currency, amount) {
    return `${getOrigin()}/${countryPath(cgId)}/stripe/paypal/returnOneTime?email=${encodeURIComponent(email)}&stripePublicKey=${stripePublicKey}&currency=${currency}&amount=${amount}`;
}
// ----- Exports ----- //
export { routes, createOneOffReminderEndpoint, createRecurringReminderEndpoint, postcodeLookupUrl, payPalCancelUrl, payPalReturnUrl, stripePayPalReturnUrl, paperSubsUrl, paperCheckoutUrl, parameteriseUrl, getDigitalPlusCheckoutDeepLink, guardianWeeklyLanding, promotionTermsUrl, };
